import { saveUtilityCache } from '../../../utils/utilityAppStorage';
import { useEffect, useState } from "react";
import { v4 as uuid } from "uuid";

const KEY = "fastfood_pos_v1";

const estadoInicial = {
  config: {
    nombreNegocio: "Mi Restaurante",
    moneda: "BOB",
    imprimeTicket: true
  },
  productos: [],
  ventas: [],
  cajas: []
};

export function useVentas() {
  const [state, setState] = useState(() => {
    const data = localStorage.getItem(KEY);
    return data ? JSON.parse(data) : estadoInicial;
  });

  // Persistencia automática
  useEffect(() => {
    saveUtilityCache(KEY, state);
  }, [state]);

  /* =========================
     CONFIGURACIÓN
  ========================== */

  const actualizarConfig = (config) => {
    setState(prev => ({
      ...prev,
      config: { ...prev.config, ...config }
    }));
  };

  /* =========================
     PRODUCTOS
  ========================== */

  const agregarProducto = ({ nombre, categoria, precio, costoEstimado = 0 }) => {
    setState(prev => ({
      ...prev,
      productos: [
        ...prev.productos,
        {
          id: uuid(),
          nombre,
          categoria,
          precio: parseFloat(precio),
          costoEstimado: parseFloat(costoEstimado),
          activo: true
        }
      ]
    }));
  };

  const desactivarProducto = (id) => {
    setState(prev => ({
      ...prev,
      productos: prev.productos.map(p =>
        p.id === id ? { ...p, activo: false } : p
      )
    }));
  };

  /* =========================
     CAJA
  ========================== */

  const abrirCaja = (montoInicial = 0) => {
    const cajaAbierta = state.cajas.find(c => !c.fechaCierre);
    if (cajaAbierta) return false;

    setState(prev => ({
      ...prev,
      cajas: [
        ...prev.cajas,
        {
          id: uuid(),
          fechaApertura: new Date().toISOString(),
          fechaCierre: null,
          montoInicial: parseFloat(montoInicial),
          montoFinal: null,
          diferencia: null
        }
      ]
    }));
    return true;
  };

  const cerrarCaja = (montoFinal) => {
    setState(prev => ({
      ...prev,
      cajas: prev.cajas.map(c => {
        if (!c.fechaCierre) {
          const totalVentas = prev.ventas
            .filter(v => !v.anulado)
            .reduce((a, b) => a + b.total, 0);

          const esperado = c.montoInicial + totalVentas;
          const diferencia = parseFloat(montoFinal) - esperado;

          return {
            ...c,
            fechaCierre: new Date().toISOString(),
            montoFinal: parseFloat(montoFinal),
            diferencia
          };
        }
        return c;
      })
    }));
  };

  /* =========================
     VENTAS (CORE)
  ========================== */

  const registrarVenta = async ({ items, metodoPago }) => {
    if (!items || items.length === 0) return false;

    const ventaId = uuid();
    const fecha = new Date().toISOString();

    const itemsNormalizados = items.map(i => ({
      productoId: i.id,
      nombre: i.nombre,
      categoria: i.categoria,
      cantidad: parseFloat(i.cantidad),
      precioUnitario: parseFloat(i.precio),
      subtotal: parseFloat(i.cantidad) * parseFloat(i.precio)
    }));

    const total = itemsNormalizados.reduce((a, b) => a + b.subtotal, 0);

    setState(prev => ({
      ...prev,
      ventas: [
        ...prev.ventas,
        {
          id: ventaId,
          fecha,
          items: itemsNormalizados,
          total,
          metodoPago,
          anulado: false
        }
      ]
    }));

    return ventaId;
  };

  const anularVenta = async (id) => {
    if (!window.confirm("¿Anular esta venta?")) return;

    setState(prev => ({
      ...prev,
      ventas: prev.ventas.map(v =>
        v.id === id ? { ...v, anulado: true } : v
      )
    }));
  };

  /* =========================
     MÉTRICAS (BASE)
  ========================== */

  const filtrarVentas = (inicio, fin) => {
    const i = new Date(inicio);
    const f = new Date(fin);

    return state.ventas.filter(v => {
      const d = new Date(v.fecha);
      return !v.anulado && d >= i && d <= f;
    });
  };

  const totalVendido = (inicio, fin) =>
    filtrarVentas(inicio, fin).reduce((a, b) => a + b.total, 0);

  const ticketPromedio = (inicio, fin) => {
    const v = filtrarVentas(inicio, fin);
    return v.length ? totalVendido(inicio, fin) / v.length : 0;
  };

  const topProductos = (inicio, fin) => {
    const map = {};

    filtrarVentas(inicio, fin).forEach(v =>
      v.items.forEach(i => {
        map[i.nombre] = (map[i.nombre] || 0) + i.cantidad;
      })
    );

    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([nombre, cantidad]) => ({ nombre, cantidad }));
  };

  /* ========================= */

  return {
    ...state,
    actualizarConfig,
    agregarProducto,
    desactivarProducto,
    abrirCaja,
    cerrarCaja,
    registrarVenta,
    anularVenta,
    filtrarVentas,
    totalVendido,
    ticketPromedio,
    topProductos
  };
}
